import { memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { HugeiconsIcon } from '@hugeicons/react'
import { NodeEditIcon, SubnodeAddIcon } from '@hugeicons-pro/core-bulk-rounded'
import { Box, Button, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import { useGetOrderItemsQuery, useUpdateOrderItemMutation } from '@/app/services/orderItems'
import { useGetOrderQuery } from '@/app/services/orders'
import { useGetSecondaryProcessesQuery } from '@/app/services/secondaryProcesses'
import { addItemToUpdateQueue, removeItemFromUpdateQueue, setSelectedOrderStatus } from '@/app/slices/orderItemsSlice'
import { selectOrganisationId, selectWebStoreFoldingEnabled } from '@/app/slices/organisationSlice'
import TbxTooltip from '@/common/components/TbxTooltip/TbxTooltip'
import { useMutationErrorSnackbar } from '@/common/hooks/useMutationErrorSnackbar'
import { ORDER_STATUS_ARRAY } from '@/common/utils'
import CustomPropertySecProcess from '@/features/secondary-processes/CustomPropertySecProcess'
import QuantitySecProcess from '@/features/secondary-processes/QuantitySecProcess'
import SecondaryProcessesModal from '@/features/secondary-processes/SecondaryProcessesModal'
import SecondaryProcessRow from '@/features/secondary-processes/SecondaryProcessRow'

import ItemFolding from './ItemFolding'

const ItemSecondaryProcesses = ({ orderItemId }) => {
    const { t } = useTranslation()
    const { showMutationError } = useMutationErrorSnackbar()
    const { orderId } = useParams()
    const dispatch = useDispatch()

    const organisationId = useSelector(selectOrganisationId)
    const foldingEnabled = useSelector(selectWebStoreFoldingEnabled)

    const { data: selectedOrder } = useGetOrderQuery({ organisationId, orderId })
    const { orderItem } = useGetOrderItemsQuery(
        {
            organisationId,
            orderId,
        },
        {
            selectFromResult: ({ data }) => ({
                orderItem: data?.find((item) => item.id === orderItemId),
            }),
        }
    )

    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
    const [showFolding, setShowFolding] = useState(orderItem?.numberOfFolds > 0)
    const [itemSecondaryProcesses, setItemSecondaryProcesses] = useState(orderItem?.secondaryProcesses)

    if (showFolding && (orderItem?.maxFoldLength === 0 || orderItem?.numberOfFolds === 0)) {
        dispatch(addItemToUpdateQueue(orderItemId))
    }

    const isRotary = useMemo(() => {
        if (!orderItem) return false
        return 'profile' in orderItem && orderItem?.profile !== ''
    }, [orderItem])

    const { data: organisationSecondaryProcesses } = useGetSecondaryProcessesQuery({ organisationId })

    const { isFetching: isFetchingOrder } = useGetOrderQuery({
        organisationId,
        orderId,
    })

    const [updateOrderItem] = useUpdateOrderItemMutation()

    const isPartFromLibrary = Boolean(orderItem?.partLibraryEntryId)

    const handleUpdateOrderItem = async (orderItem) => {
        try {
            // Backend sets the order to NotCalculated as part of the item update (and updateOrderItem
            // refetches the order), so no separate updateOrder PUT is needed here. The optimistic status
            // is already dispatched by the handlers below.
            await updateOrderItem({
                organisationId,
                orderId,
                orderItemId: orderItem?.id,
                orderItem,
            }).unwrap()
        } catch (error) {
            showMutationError(error, t('Failed to update the order item'))
        }
    }

    const handleEditButtonClick = (event) => {
        event.stopPropagation()
        setIsEditDialogOpen(true)
    }

    const onSaveCloseEditDialog = async (processes) => {
        setIsEditDialogOpen(false)
        dispatch(addItemToUpdateQueue(orderItemId))
        try {
            //handle empty processes
            if (!processes?.length) {
                setItemSecondaryProcesses([])
                await handleUpdateOrderItem({
                    ...orderItem,
                    secondaryProcesses: [],
                    numberOfFolds: 0,
                    maxFoldLength: 0,
                    foldingSource: 'Manual',
                })
            }

            if (processes?.length) {
                const hasFolding = processes.find((process) => process.secondaryProcessId === 'Folding')
                const otherProcesses = processes?.filter((process) => process.secondaryProcessId !== 'Folding')

                if (hasFolding) {
                    setShowFolding(true)
                } else {
                    setShowFolding(false)
                }

                setItemSecondaryProcesses(otherProcesses)
                await handleUpdateOrderItem({
                    ...orderItem,
                    secondaryProcesses: otherProcesses,
                    numberOfFolds: hasFolding ? orderItem.numberOfFolds || 1 : 0,
                    maxFoldLength: hasFolding ? orderItem.maxFoldLength || 0 : 0,
                    foldingSource: hasFolding ? orderItem.foldingSource || 'Manual' : 'Manual',
                })
                if (selectedOrder.status !== ORDER_STATUS_ARRAY.NotCalculated) {
                    dispatch(setSelectedOrderStatus(ORDER_STATUS_ARRAY.NotCalculated))
                }
            }
        } finally {
            dispatch(removeItemFromUpdateQueue(orderItemId))
        }
    }

    const onInputValueChange = async (process) => {
        dispatch(addItemToUpdateQueue(orderItemId))
        if (selectedOrder.status !== ORDER_STATUS_ARRAY.NotCalculated) {
            dispatch(setSelectedOrderStatus(ORDER_STATUS_ARRAY.NotCalculated))
        }

        const processIds = itemSecondaryProcesses?.map((sp) => sp.secondaryProcessId)
        const processIndex = processIds.indexOf(process.secondaryProcessId)
        const newProcessList = [...itemSecondaryProcesses]
        newProcessList[processIndex] = process
        setItemSecondaryProcesses(newProcessList)

        await handleUpdateOrderItem({ ...orderItem, secondaryProcesses: newProcessList })
        dispatch(removeItemFromUpdateQueue(orderItemId))
    }

    const getProcessDetails = useCallback(
        (processId) => organisationSecondaryProcesses?.find((osp) => osp.secondaryProcessId === processId) || {},
        [organisationSecondaryProcesses]
    )

    const sortedProcesses = useMemo(() => {
        return itemSecondaryProcesses
            ?.map((process) => ({
                ...process,
                index: getProcessDetails(process.secondaryProcessId)?.index,
                customPropertyName: getProcessDetails(process.secondaryProcessId)?.customPropertyName,
                customPropertyOptions: getProcessDetails(process.secondaryProcessId)?.customPropertyOptions,
                customValue: process.customValue,
            }))
            .sort((a, b) => (a.index < b.index ? -1 : 1))
    }, [itemSecondaryProcesses, getProcessDetails])

    const hasEnabledCustomProcesses = useMemo(() => {
        return (
            organisationSecondaryProcesses?.some(
                (process) => !process.isDeleted && process.webStoreEnabled && process.customerVisible
            ) ?? false
        )
    }, [organisationSecondaryProcesses])

    return (
        <Box
            key={`item-secondary-processes-${orderItemId}`}
            sx={{
                display: 'flex',
                flex: '1',
                flexDirection: 'column',
                width: 1,
                gap: 2,
            }}
        >
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    width: '100%',
                }}
            >
                <Typography
                    data-testid="secondary-processes-section-title"
                    variant="strong1"
                >
                    {t('Secondary processes')}
                </Typography>

                {itemSecondaryProcesses?.length || showFolding ? (
                    <TbxTooltip
                        title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                        arrow
                    >
                        <span>
                            <Button
                                aria-label="edit"
                                color="primary"
                                data-testid="secondary-processes-edit-button"
                                disabled={isFetchingOrder || isPartFromLibrary}
                                size="small"
                                startIcon={
                                    <HugeiconsIcon
                                        icon={NodeEditIcon}
                                        size={21}
                                    />
                                }
                                title={t('Edit secondary processes')}
                                onClick={(event) => handleEditButtonClick(event)}
                                onFocus={(event) => event.stopPropagation()}
                            >
                                {t('Edit')}
                            </Button>
                        </span>
                    </TbxTooltip>
                ) : null}
            </Box>

            {!itemSecondaryProcesses?.length &&
            !showFolding &&
            (hasEnabledCustomProcesses || (!isRotary && foldingEnabled)) ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                    <Button
                        data-testid="secondary-processes-add-button"
                        disabled={isFetchingOrder || isPartFromLibrary}
                        startIcon={<HugeiconsIcon icon={SubnodeAddIcon} />}
                        variant="outlined"
                        onClick={(event) => handleEditButtonClick(event)}
                    >
                        {t('Add secondary processes')}
                    </Button>
                </Box>
            ) : null}

            {!itemSecondaryProcesses?.length &&
            !showFolding &&
            !hasEnabledCustomProcesses &&
            (isRotary || !foldingEnabled) ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                    <Typography
                        color="text.secondary"
                        variant="body2"
                    >
                        {t('No secondary processes available')}
                    </Typography>
                </Box>
            ) : null}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {showFolding && !isRotary ? (
                    <ItemFolding
                        key={`item-folding-${orderItemId}`}
                        orderItemId={orderItemId}
                    />
                ) : null}

                {sortedProcesses?.map((process) => (
                    <SecondaryProcessRow
                        justifyContent="space-between"
                        key={process.secondaryProcessId}
                    >
                        <Typography
                            data-testid="order-item-secondary-process-label"
                            variant="body2"
                        >
                            {getProcessDetails(process.secondaryProcessId)?.name}
                        </Typography>
                        <Box
                            gap={1.5}
                            sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
                        >
                            {getProcessDetails(process.secondaryProcessId)?.customPropertyName ? (
                                <CustomPropertySecProcess
                                    isPartFromLibrary={isPartFromLibrary}
                                    orderItemId={orderItemId}
                                    process={process}
                                    onInputChange={onInputValueChange}
                                />
                            ) : null}

                            {getProcessDetails(process.secondaryProcessId)?.measureType === 'Quantity' ? (
                                <QuantitySecProcess
                                    isPartFromLibrary={isPartFromLibrary}
                                    orderItemId={orderItemId}
                                    process={process}
                                    onInputChange={onInputValueChange}
                                />
                            ) : null}
                        </Box>
                    </SecondaryProcessRow>
                ))}
            </Box>
            {isEditDialogOpen ? (
                <SecondaryProcessesModal
                    foldingEnabled={foldingEnabled}
                    isRotary={isRotary}
                    open={isEditDialogOpen}
                    orderItemId={orderItemId}
                    secondaryProcesses={orderItem?.secondaryProcesses}
                    showFolding={showFolding}
                    onSaveClose={onSaveCloseEditDialog}
                />
            ) : null}
        </Box>
    )
}

ItemSecondaryProcesses.propTypes = {
    orderItemId: PropTypes.string,
}

export default memo(ItemSecondaryProcesses)
